import { Button, Modal } from "@legal-500/legal500-design-system";
import type { KeyspaceDirectoryEntry } from "@legal-500/redis-db-manager-types";
import { SlotReleaseDetails } from "./SlotReleaseDetails.js";
import { UserLabel } from "./UserLabel.js";
import { formatDurationMs, formatTimestamp } from "./slotStatus.js";
import styles from "./DbManager.module.css";

type SlotDetailModalProps = {
  isOpen: boolean;
  entry: KeyspaceDirectoryEntry | null;
  onClose: () => void;
};

export function SlotDetailModal({ isOpen, entry, onClose }: SlotDetailModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={entry ? `Slot: ${entry.keyspace}` : "Slot detail"}
      size="large"
      blurBackground
    >
      <div className={styles.modalBody}>
        {entry && (
          <>
            <section>
              <h3>Per-page-type stats</h3>
              <PageTypeTable entry={entry} />
            </section>

            <section>
              <h3>Recent failures</h3>
              <FailureList entry={entry} />
            </section>

            <section>
              <h3>Release</h3>
              <SlotReleaseDetails
                releaseDefinition={entry.releaseDefinition}
                projectionVersion={entry.projectionVersion}
              />
            </section>

            <section>
              <h3>Sync metadata</h3>
              <ul>
                <li>
                  <strong>Status:</strong> {entry.status}
                </li>
                <li>
                  <strong>Triggered by:</strong> <UserLabel value={entry.triggeredBy} />
                </li>
                <li>
                  <strong>Started:</strong> {formatTimestamp(entry.startedAt)}
                </li>
                <li>
                  <strong>Finished:</strong> {formatTimestamp(entry.finishedAt)}
                </li>
                <li>
                  <strong>Snapshot:</strong> {entry.source.snapshotS3Path || "-"}
                </li>
                <li>
                  <strong>Snapshot checksum:</strong> {entry.source.snapshotChecksum || "-"}
                </li>
                <li>
                  <strong>Sync git SHA:</strong> {entry.source.syncCommit || "-"}
                </li>
                {entry.source.seededFromKeyspace !== undefined && (
                  <li>
                    <strong>Seeded from:</strong> <code>{entry.source.seededFromKeyspace}</code>
                  </li>
                )}
                <li>
                  <strong>Doc keys:</strong> {entry.totals.docKeyCount.toLocaleString()}
                </li>
                <li>
                  <strong>Total duration:</strong> {formatDurationMs(entry.totals.durationMs)}
                </li>
              </ul>
            </section>

            <details className={styles.expander}>
              <summary>Raw metadata JSON</summary>
              <div className={styles.expanderInner}>
                <pre className={styles.rawJson}>{JSON.stringify(entry, null, 2)}</pre>
              </div>
            </details>
          </>
        )}

        <div className={styles.actions}>
          <Button variant="contained" color="default" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PageTypeTable({ entry }: { entry: KeyspaceDirectoryEntry }) {
  if (entry.pageTypes.length === 0) {
    return <p>No page-type stats available for this slot.</p>;
  }

  return (
    <table className={styles.detailTable}>
      <thead>
        <tr>
          <th>Page type</th>
          <th>Expected</th>
          <th>Succeeded</th>
          <th>Failed</th>
          <th>Duration</th>
          <th>Success %</th>
        </tr>
      </thead>
      <tbody>
        {entry.pageTypes.map(pt => {
          const successRate = computeSuccessRate(pt.succeededCount, pt.expectedCount);
          return (
            <tr key={pt.name}>
              <td>{pt.name}</td>
              <td>{pt.expectedCount.toLocaleString()}</td>
              <td>{pt.succeededCount.toLocaleString()}</td>
              <td>{pt.failedCount.toLocaleString()}</td>
              <td>{formatDurationMs(pt.durationMs)}</td>
              <td>{successRate}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function FailureList({ entry }: { entry: KeyspaceDirectoryEntry }) {
  if (entry.failures.length === 0) {
    return <p>No failures recorded.</p>;
  }
  return (
    <div className={styles.failureList}>
      {entry.failures.map((failure, idx) => (
        <div className={styles.failureItem} key={`${failure.pageType}-${failure.path}-${idx}`}>
          <strong>{failure.pageType}</strong> — {failure.path} ({failure.locale}) — attempts:{" "}
          {failure.attempts}
          <div>{failure.error}</div>
        </div>
      ))}
    </div>
  );
}

function computeSuccessRate(succeeded: number, expected: number): string {
  if (expected <= 0) return "-";
  const ratio = (succeeded / expected) * 100;
  return `${ratio.toFixed(1)}%`;
}
